/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import NotificationBell from "../../../../components/Notification/NotificationBell";
import TertiaryTopRoundButton from "../../../../components/_utils/CommonButtons/TertiaryTopRoundButton/TertiaryTopRoundButton";
import TopBarButtons from "../../../../components/_utils/CommonButtons/TopBarButton";
import CommonCardDashboard from "../../../../components/_utils/CommonCardDashboard";
import CommonMoreActionsCard from "../../../../components/_utils/CommonMoreActionsCard";
import WeeklySchedulesCard from "../../../../components/_utils/WeeklySchedulesCard";
import TransactionCounts from "../../../../components/Settings/Statistics/GraphsComponents/TransactionCounts";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import { beneficiaryTransactionsViewAll } from "../../../../redux/features/transactions/beneficiaryTransactions/index.js";
import { scheduledBeneficiaryViewAll } from "../../../../redux/features/transactions/scheduleBeneficiaryTransactions/index.js";
import {
  GlobalConfiguration,
  PaginationConfiguration,
  TimelineConfiguration,
} from "../../../../utils/configurationProvider";
import globalText from "../../../../utils/globalText";
import useInitials from "../../../../utils/useInitials.js";

const BeneficiaryTransactionDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [txnCountLoading, setTxnCountLoading] = useState(true);
  const [pendingScheduleCount, setPendingScheduleCount] = useState(0);
  const initials = useInitials();
  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);
  const services = useSelector((state) => state.refreshToken.services);
  const {
    beneficiaryTransactionsViewAllData,
    beneficiaryTransactionsViewAllLoading,
  } = useSelector((state) => state.beneficiaryTransactions);

  const { scheduledBeneficiaryViewData, scheduledBeneficiaryViewLoading } =
    useSelector((state) => state?.scheduleBeneficiaryTransactions);

  const totalCount = beneficiaryTransactionsViewAllData?.total_count ?? 0;

  const requireApprovalTxnCount =
    beneficiaryTransactionsViewAllData?.summary?.actionable_transaction_count;

  const pendingTxnCount =
    beneficiaryTransactionsViewAllData?.summary?.pending_transaction_count;

  const approvedTxnCount =
    beneficiaryTransactionsViewAllData?.summary?.approved_transaction_count;

  const onMount = () => {
    (async () => {
      await Promise.allSettled([
        dispatch(
          beneficiaryTransactionsViewAll({
            page: GlobalConfiguration?.lengthCheckOne,
            page_size: PaginationConfiguration.paginationSmall,
          }),
        ),
        dispatch(
          scheduledBeneficiaryViewAll({
            page: GlobalConfiguration?.lengthCheckOne,
            page_size: PaginationConfiguration.paginationSmall,
          }),
        ),
      ]);
      setTxnCountLoading(false);
    })();
  };

  const rejectedTxnCount =
    beneficiaryTransactionsViewAllData?.summary?.rejected_transaction_count ??
    GlobalConfiguration.dashboardPrimaryCount;

  const fetchPendingScheduleCount = async () => {
    try {
      const response = await dispatch(
        scheduledBeneficiaryViewAll({ lifecycle_status: "pending" }),
      );
      setPendingScheduleCount(response?.payload?.total_count || 0);
    } catch (error) {
      console.error("Error fetching pending schedule count:", error);
      setPendingScheduleCount(0);
    }
  };

  const isViewAllDisabled =
    !services?.includes("VBeneficiaryTxn") ||
    beneficiaryTransactionsViewAllLoading ||
    txnCountLoading ||
    totalCount === GlobalConfiguration?.lengthCheckZero;

  useEffect(() => {
    dispatch(enableSwitcher());
    return () => {
      dispatch(disableSwitcher());
    };
  }, []);

  useEffect(() => {
    fetchPendingScheduleCount();
    onMount();
  }, [markerData]);

  const handleDatacardClick = (approvalStatus) => {
    const routeMap = {
      approved:
        "/beneficiary_transaction_dashboard/beneficiary_transaction_view_all",
      pending:
        "/beneficiary_transaction_dashboard/beneficiary_transaction_view_all",
      require:
        "/beneficiary_transaction_dashboard/beneficiary_transaction_single_approval",
    };

    navigate(routeMap[approvalStatus], {
      state: {
        approvalStatus,
      },
    });
  };

  // Function to check if a given date falls within the current week
  // TODO: Open to improvements — if anyone has a better or more accurate way to check weekly range, feel free to refactor.
  const isInThisWeek = (dateString) => {
    const date = dayjs(dateString);
    const startOfWeek = dayjs()?.startOf("week");
    const endOfWeek = dayjs()?.endOf("week");
    return (
      date?.isAfter(
        startOfWeek?.subtract(GlobalConfiguration?.lengthCheckOne, "day"),
      ) &&
      date?.isBefore(endOfWeek?.add(GlobalConfiguration?.lengthCheckOne, "day"))
    );
  };

  const thisWeekTransactions = Array?.isArray(
    scheduledBeneficiaryViewData?.transactions,
  )
    ? scheduledBeneficiaryViewData?.transactions?.filter((txn) =>
        isInThisWeek(txn?.transaction_start_date),
      )
    : [];
  const formatPendingCount = (count, services, serviceName) => {
    const hasService = (services || []).includes(serviceName);

    if (!hasService) {
      return count > GlobalConfiguration?.lengthCheckZero ? "N/A" : "00";
    }

    if (count === GlobalConfiguration?.lengthCheckZero) return "00";

    const digitCount = count.toString().length;

    // If 3 digits or less → show normally
    if (digitCount <= GlobalConfiguration?.categoryminLength) {
      return count
        .toLocaleString("en-IN")
        .padStart(TimelineConfiguration?.barWidthMedium, "0");
    }

    // Max number with one less digit
    const maxForDigits =
      Math.pow(
        GlobalConfiguration?.panLength,
        digitCount - GlobalConfiguration?.lengthCheckOne,
      ) - GlobalConfiguration?.lengthCheckOne;

    return `${maxForDigits.toLocaleString("en-IN")}+`;
  };
  const handleCreateSchedule = () => {
    const requiredServices = ["BCScheduledTxn", "BOScheduledTxn"];
    const missingServices = requiredServices.filter(
      (service) => !(services || []).includes(service),
    );

    if (missingServices.length === 0) {
      navigate("/beneficiary_transaction_dashboard/create_schedule");
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: missingServices },
      });
    }
  };
  const handleViewSchedules = () => {
    if ((services || []).includes("BVScheduledTxn")) {
      navigate("/beneficiary_transaction_dashboard/schedule_view_all");
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: "BVScheduledTxn" },
      });
    }
  };

  const schedulesLoading =
    scheduledBeneficiaryViewLoading ||
    scheduledBeneficiaryViewData === undefined ||
    scheduledBeneficiaryViewData === null;

  const schedules = Array.isArray(thisWeekTransactions)
    ? thisWeekTransactions
    : [];
  const safeServices = Array.isArray(services) ? services : [];
  const approvalService = "BAScheduledTxn";

  const canApprove = safeServices.includes(approvalService);
  const handleRequireApprovalNavigation = () => {
    if (!canApprove) {
      navigate("/feature_not_assigned", {
        state: { unavailableService: approvalService },
      });
      return;
    }
    navigate("/beneficiary_transaction_dashboard/schedule_single_approval");
  };

  const navigateWithServiceCheck = ({
    service,
    route,
    unavailableService = service,
  }) => {
    if (!services?.includes(service)) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService,
        },
      });
      return;
    }
    navigate(route);
  };

  const handleScheduleClick = (txnId) => {
    navigate(
      `/beneficiary_transaction_dashboard/schedule_view_all/schedule_detail_page/${txnId}`,
      {
        state: { txnId },
      },
    );
  };
  return (
    <div
      style={{ height: "var(--full-height)" }}
      className="bg-primary-bg w-width-full px-padding-md overflow-y-auto lg:px-padding-base pb-padding-md"
    >
      <div className="h-navbar-height Qsmd:h-navbar-breadcrumb-height"></div>
      <div className="pb-padding-lg">
        <div className="flex flex-col mt-margin-md Qlg:mx-auto pb-padding-xl Qlg:pb-padding-base max-w-tab-width-normal w-width-full Qlg:flex-row Qlg:px-padding-base mb-margin-md Qlg:mb-margin-base Qlg:relative gap-gap-md">
          <div className="Qlg:w-dashboard-left-card-width flex flex-col">
            <div className="Qsmd:hidden w-width-full fixed top-top-base left-left-base right-right-base bg-text-primary flex items-center justify-between z-z-index-fixed-ui rounded-b-border-radius-sm h-[var(--home-nav-mobile-height)] pl-mobile-padding-l pr-mobile-padding-l border-b border-primary-bg shadow-sm">
              <div className="flex items-center">
                <img
                  src={ICONS?.XPENTRA_BLUE}
                  alt="Xpentra Logo"
                  className="w-xpentra-logo-width h-xpentra-logo-height"
                />
              </div>
              <div className="flex gap-gap-sm flex-row items-center ">
                <NotificationBell />
                <div className="w-px h-5 bg-primary-card mx-2"></div>
                <div
                  className="text-xs bg-text-secondary text-primary-bg h-[var(--profile-initials-w-h)] w-[var(--profile-initials-w-h)] flex items-center justify-center rounded-full font-semibold cursor-pointer"
                  onClick={() => navigate("/home/profile_quick_menu")}
                >
                  {initials}
                </div>
              </div>
            </div>
            <TopBarButtons
              services={services}
              items={[
                {
                  imgColor: "var(--text-primary)",
                  icon: ICONS?.ADD_BUTTON_WHITE,
                  label: <>Create beneficiary transaction</>,
                  requiredservice: ["CBeneficiaryTxn"],
                  path: "/beneficiary_transaction_dashboard/create_beneficiary_transaction",
                  onClick: () => sessionStorage.removeItem("recreate_ben"),
                },
                {
                  imgColor: "var(--text-primary)",
                  icon: ICONS?.ADD_BUTTON_WHITE,
                  label: <>Create bulk transaction</>,
                  requiredservice: ["CTransactionUpload"],
                  path: "/beneficiary_transaction_dashboard/create_bulk_transaction",
                  state: { type: "bulk" },
                },
                {
                  icon:
                    rejectedTxnCount > GlobalConfiguration?.lengthCheckZero
                      ? ICONS?.ALERT
                      : ICONS?.ALERT_WHITE,
                  label: <>Recreate transactions</>,
                  requiredservice: ["VBeneficiaryTxn"],
                  count: rejectedTxnCount,
                  countLoading: beneficiaryTransactionsViewAllLoading,
                  path: "/beneficiary_transaction_dashboard/recreate_beneficiary_transaction_dashboard",
                },
              ]}
            />

            <div className="w-width-full bg-primary-card mt-margin-md rounded-border-radius-md relative overflow-hidden">
              <div className="bg-primary-card flex items-center justify-between pl-padding-md text-text-primary transition-all duration-animation-medium">
                <div className="flex items-center text-sm font-semibold">
                  <img
                    src={ICONS?.APPROVAL_STATUS}
                    alt="transaction header"
                    className="w-icon-sm mr-margin-sm"
                  />
                  Approval statuses
                </div>
                <div className="flex">
                  <div className="border-dividing"></div>
                  <TertiaryTopRoundButton
                    btnText={globalText?.VIEW_ALL}
                    icon={ICONS?.ARROW_RIGHT_WHITE}
                    imgColor="var(--text-primary)"
                    btnType="button"
                    hoverOption="white"
                    handleFunction={() => {
                      if (isViewAllDisabled) return;
                      const hasService =
                        Array.isArray(services) &&
                        services.includes("VBeneficiaryTxn");
                      if (!hasService) {
                        navigate("/feature_not_assigned", {
                          state: { unavailableService: "VBeneficiaryTxn" },
                        });
                        return;
                      }
                      navigate(
                        "/beneficiary_transaction_dashboard/beneficiary_transaction_view_all",
                      );
                    }}
                    isDisabled={isViewAllDisabled}
                    enabledTextColor={"var(--text-primary)"}
                    enabledBgColor={"var(--primary-card)"}
                    disabledBgColor={"transparent"}
                  />
                </div>
              </div>

              <div className="flex flex-col Qlg:flex-row gap-gap-base p-padding-base bg-primary-nav">
                <CommonCardDashboard
                  label={globalText?.REQUIRE_APPROVAL}
                  count={
                    services?.includes("ABeneficiaryTxn")
                      ? requireApprovalTxnCount || "00"
                      : "N/A"
                  }
                  color="var(--pending)"
                  loader={
                    txnCountLoading || beneficiaryTransactionsViewAllLoading
                  }
                  onClick={() => {
                    navigate(
                      "/beneficiary_transaction_dashboard/beneficiary_transaction_single_approval",
                    );
                  }}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "ABeneficiaryTxn" },
                    })
                  }
                />

                <div className="border-border border-primary-bg Qlg:my-margin-md my-margin-base" />
                <CommonCardDashboard
                  label={globalText?.PENDING_TRANSACTIONS}
                  count={
                    services?.includes("VBeneficiaryTxn")
                      ? (pendingTxnCount ?? "00")
                      : "N/A"
                  }
                  color="var(--pending)"
                  loader={
                    txnCountLoading || beneficiaryTransactionsViewAllLoading
                  }
                  onClick={() => handleDatacardClick("pending")}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "VBeneficiaryTxn" },
                    })
                  }
                />
                <div className="border-border border-primary-bg Qlg:my-margin-md my-margin-base" />
                <CommonCardDashboard
                  label={globalText?.APPROVED_TRANSACTIONS}
                  count={
                    services?.includes("VBeneficiaryTxn")
                      ? (approvedTxnCount ?? "00")
                      : "N/A"
                  }
                  color="var(--awaiting)"
                  loader={
                    txnCountLoading || beneficiaryTransactionsViewAllLoading
                  }
                  onClick={() => handleDatacardClick("approved")}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "VBeneficiaryTxn" },
                    })
                  }
                />
              </div>
            </div>

            <div className="w-full mt-margin-md bg-primary-nav rounded-t-border-radius-md rounded-b-border-radius-md">
              <div className="relative w-full overflow-hidden h-height-full">
                <div className="relative w-full z-z-index-base">
                  <TransactionCounts
                    autoFetch={true}
                    flow="bene"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col Qlg:w-dashboard-right-card-width gap-y-gap-md lg:absolute Qlg:h-full Qlg:right-right-base">
            <WeeklySchedulesCard
              header={{
                title: "Schedules for this week",
                icon: ICONS.CALENDER,
              }}
              approval={{
                service: "BAScheduledTxn",
                count: pendingScheduleCount,
                label: "Require approval",
                onClick: handleRequireApprovalNavigation,
              }}
              list={{
                loading: schedulesLoading,
                emptyText: (
                  <>
                    There are no transactions scheduled for <br />
                    this week.
                  </>
                ),
                data: schedules,
                getTitle: (row) => row.scheduled_name,
                getID: (row) => row?.txn_id,
                getDate: (row) =>
                  dayjs(row.transaction_start_date).format("DD MMM YYYY"),
                titleLabel: "Schedule name",
                dateLabel: "Transaction initiation date",
                handleClick: handleScheduleClick,
              }}
              services={services}
              formatPendingCount={formatPendingCount}
              onFeatureNotAssigned={(missingServices) =>
                navigate("/feature_not_assigned", {
                  state: { unavailableService: missingServices },
                })
              }
              actions={[
                {
                  label: "Create schedule",
                  service: ["BCScheduledTxn", "BOScheduledTxn"],
                  onClick: handleCreateSchedule,
                  enabledBgColor: "var(--primary-nav-act)",
                  enabledTextColor: "var(--text-primary)",
                  outLineColor: "",
                  hoverOption: "white",
                  fullWidthRequired: true,
                },
                {
                  label: "View schedules",
                  service: "BVScheduledTxn",
                  onClick: handleViewSchedules,
                  enabledBgColor: "var(--primary-nav-act)",
                  enabledTextColor: "var(--text-primary)",
                  outLineColor: "",
                  hoverOption: "white",
                  fullWidthRequired: true,
                },
              ]}
            />

            <div className="w-full ">
              <CommonMoreActionsCard
                title="More actions"
                actions={[
                  {
                    label: "Bulk transactions view all",
                    service: "VTransactionUpload",
                    onClick: () =>
                      navigateWithServiceCheck({
                        service: "VTransactionUpload",
                        route:
                          "/beneficiary_transaction_dashboard/bulk_transaction_view_all",
                      }),
                  },
                  {
                    label: "MIS report",
                    service: "SMisUser",
                    onClick: () =>
                      navigateWithServiceCheck({
                        service: "SMisUser",
                        route:
                          "/beneficiary_transaction_dashboard/generate_mis_report",
                      }),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryTransactionDashboard;
